import type { HTMLProps } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDropdownContext } from '~/components/Dropdown/hooks/useDropdownContext';
import { Spinner } from '~/components/Spinner';
import { cn } from '~/lib/cn';

import { useItemEventHandlers } from '../../hooks/useItemEventHandlers';
import { DropdownItem } from '../DropdownItem';

type DropdownContentProps = HTMLProps<HTMLDivElement>;

export function DropdownContent({
  children,
  className,
  ...props
}: DropdownContentProps) {
  const {
    collection,
    selected,
    multiselect,
    isSelected,
    maxSelectedOptions,
    loadMoreDistance,
    onLoadMore,
    noOptionsText,
    loading,
  } = useDropdownContext();
  const createHandlers = useItemEventHandlers();

  const canSelectMore =
    !multiselect ||
    maxSelectedOptions === undefined ||
    selected.length < maxSelectedOptions;

  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleLoadMore = useCallback(async () => {
    if (!onLoadMore || loadMoreLoading) {
      return;
    }

    setLoadMoreLoading(true);
    await onLoadMore();
    setLoadMoreLoading(false);
  }, [onLoadMore, loadMoreLoading]);

  useEffect(() => {
    if (!sentinelRef.current || !containerRef.current || !onLoadMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: containerRef.current,
        rootMargin: `${loadMoreDistance}px`,
        threshold: 0,
      },
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [handleLoadMore, loadMoreDistance, onLoadMore]);

  return (
    <div
      role="listbox"
      className={cn([
        'rounded-xs',
        'bg-level-01',
        'w-[var(--radix-popover-trigger-width)]',
        'mt-xs',
        'shadow',
        'border',
        'border-emphasis-strong',
      ])}
      {...props}
    >
      {children}
      <div
        ref={containerRef}
        onWheel={(e) => e.stopPropagation()}
        className={cn(['overflow-auto', className])}
      >
        {collection.map(
          ({ label, value, ref, render, disabled: optDisabled, ariaLabel }) => {
            const selectedNow = isSelected(value);
            const disabledNow = canSelectMore
              ? Boolean(optDisabled)
              : !selectedNow;

            return (
              <DropdownItem
                key={value}
                checked={isSelected(value)}
                aria-label={typeof label === 'string' ? label : ariaLabel}
                isCheckbox={multiselect}
                className={cn({ 'bg-level-03': isSelected(value) })}
                disabled={canSelectMore ? false : !isSelected(value)}
                {...createHandlers(value)}
                ref={ref}
              >
                {render
                  ? render({ selected: selectedNow, disabled: disabledNow })
                  : label}
              </DropdownItem>
            );
          },
        )}
        {collection.length === 0 && !loading && noOptionsText ? (
          <span className="flex justify-self-center px-md py-sm text-center font-normal text-default">
            {noOptionsText}
          </span>
        ) : null}
        {onLoadMore ? <div ref={sentinelRef} /> : null}
        {loadMoreLoading || loading ? (
          <div className="flex justify-center px-md py-sm text-default">
            <Spinner size="sm" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

DropdownContent.displayName = 'DropdownContent';
